import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { ChartMode } from '../types';
import { assertNotNil } from '../../../utils/types';
import {
  IAnalysisEntityPermissions,
  IAnalysisEntityPermissionsHint,
} from '../../../utils/hooks/permissionHooks/types';
import {
  IAnalysisMetadata,
  IAnalysisSettings,
  IAnalysisState,
} from '../../../redux/slices/analyses';
import { applyAliasReplacements } from '../../../utils/redux';
import { createEmptyChartData } from '../../../utils/redux/util';
import { QueryType } from '../../../utils/apollo/graphqlSchema';
import {
  getDisplayedMeasures,
  getDisplayedSlices,
  queryTypesUseBaseData,
} from './utils';
import {
  IDisplayedMeasure,
  IDisplayedSlice,
  IParsedChartData,
} from '../interfaces';
import {
  generateAnalysisColorConfig,
  IAnalysisColorConfig,
} from '../utils/analysisColorConfig';
import useAnalysisEntityPermission from '../../../utils/hooks/permissionHooks/useAnalysisEntityPermission';
import { isTransientAnalysisId } from '../../../redux/utils/transientAnalysis';
interface IProps {
  children: ReactNode;
  analysis: IAnalysisState;
  mode: ChartMode;
  chartOptionsEnabled?: boolean;
  onDelete?: () => void;
  dragHandleElement?: ReactNode;
  isEmbedded?: boolean;
  rebuildChart?: () => void;
}

interface IChartMetadata extends IAnalysisMetadata {
  mode: ChartMode;
  chartOptionsEnabled: boolean;
  dragHandleElement?: ReactNode;
  hasUpdateDisplayOrDescription: boolean;
  isEmbedded: boolean;
}

interface ITopPathsContext {
  metadata: IChartMetadata;
  chartData: IParsedChartData;
  settings: IAnalysisSettings;
  colorConfig: IAnalysisColorConfig;
  displayedMeasures: IDisplayedMeasure[];
  displayedSlices: IDisplayedSlice[];
  __typename: 'TopPathsContext';
}

interface IBaseChartContext {
  metadata: IChartMetadata;
  actions: {
    onDelete?: () => void;
    updateDisplay: (display: string) => void;
    updateDescription: (description: string) => void;
    rebuildChart?: () => void;
  };
  chartData: IParsedChartData;
  settings: IAnalysisSettings;
  colorConfig: IAnalysisColorConfig;
  /**
   * IMPORTANT: DisplayedMeasures is the primary source for measure configurations.
   * This property contains the formatted measure settings that was selected between
   * Central Chart Settings and individual measure format.
   */
  displayedMeasures: IDisplayedMeasure[];
  displayedSlices: IDisplayedSlice[];
  permissions: {
    entityPermissions: IAnalysisEntityPermissions;
    entityPermissionsHint: IAnalysisEntityPermissionsHint;
  };

  __typename: 'BaseChartContext';
}

const ChartContext = createContext<IBaseChartContext | ITopPathsContext | null>(
  null,
);

/**
 * useChartContext will return topPathContext if exists, otherwise return baseChartContext
 * This will support the code structure of top path which have nested <Chart /> components
 */
export const useChartContext = (): ITopPathsContext | IBaseChartContext => {
  const contextValue = useContext(ChartContext);
  assertNotNil(contextValue);

  if (contextValue.__typename === 'TopPathsContext') {
    return contextValue;
  } else if (contextValue.__typename === 'BaseChartContext') {
    return contextValue;
  } else {
    throw new Error('No chart context found');
  }
};

/**
 * This context is going to wrap ChartContainer to avoid passing values from
 * top level to subcomponents.
 *
 * The case we cannot use this context is outside of <Chart /> component.
 * for example:
 * 1. access AnalysisInfo in ExecutedHistory, access current analysis from formula,
 * 2. access current analysis from report page.
 * 3. AI feature on right sidebar.
 *
 * The context also maintain the updated settings upon user actions,
 * for example, alias, color, enable/disable measure/groups, etc.
 *
 * How to handle TopPaths:
 * Nested Context in TopPathFunnelChart.tsx,
 * if it's transientAnalysisId, store the context in ChartContext.topPathContext
 * Otherwise, store the context into ChartContext.baseChartContext
 */
export const ChartContextProvider = ({
  children,
  analysis,
  mode,
  dragHandleElement,
  chartOptionsEnabled = true,
  onDelete,
  isEmbedded = false, // flag to indicate if the chart is externally embedded in a third party app
  rebuildChart,
}: IProps) => {
  // use baseChartData to indicate the chartData is not applied alias replacements
  const { metadata, chartData: baseChartData, settings } = analysis;
  assertNotNil(metadata);
  assertNotNil(settings);

  // use to update the display and description of the chart
  // to avoid refetch entire analysis from Kobol
  const [display, updateDisplay] = useState(metadata.display);
  const [description, updateDescription] = useState(metadata.description);

  const { displayedMeasureSettings, displayedSliceSettings, axisSettings } =
    settings.chartSettings;
  const { queryType } = metadata;

  const { entityPermissions, entityPermissionsHint } =
    useAnalysisEntityPermission(analysis);

  const chartData = useMemo(() => {
    if (!baseChartData) {
      return createEmptyChartData(queryType);
    }

    if (queryTypesUseBaseData.includes(queryType)) {
      return baseChartData;
    }
    return applyAliasReplacements(baseChartData);
  }, [baseChartData, queryType]);

  const displayedMeasures = useMemo<IDisplayedMeasure[]>(() => {
    switch (chartData?.queryType) {
      case QueryType.Expression:
      case QueryType.Funnel:
      case QueryType.DataTable:
        return getDisplayedMeasures({
          displayedMeasureSettings,
          measureControls: chartData.measureControls,
          queryType: chartData.queryType,
          axisSettings,
        });
      default:
        return [];
    }
  }, [chartData, displayedMeasureSettings, axisSettings]);

  const displayedSlices = useMemo<IDisplayedSlice[]>(() => {
    switch (chartData?.queryType) {
      case QueryType.Expression:
      case QueryType.Funnel:
      case QueryType.Retention:
      case QueryType.Path:
      case QueryType.DataTable:
        return getDisplayedSlices(displayedSliceSettings, chartData.slices);
      default:
        return [];
    }
  }, [chartData, displayedSliceSettings]);

  const colorConfig = useMemo(() => {
    const isBreakdownOverall = metadata.isBreakdownOverall;
    const hasOverall = Boolean(
      metadata.query &&
        'breakdown' in metadata.query &&
        metadata.query.breakdown?.overall,
    );
    return generateAnalysisColorConfig({
      displayedMeasureSettings,
      displayedSliceSettings,
      chartData,
      isBreakdownOverall,
      hasOverall,
    });
  }, [displayedMeasureSettings, displayedSliceSettings, chartData, metadata]);

  const contextValues: IBaseChartContext | ITopPathsContext = useMemo(() => {
    if (isTransientAnalysisId(metadata.analysisId)) {
      return {
        metadata: {
          ...metadata,
          display,
          description,
          mode,
          // no modification on TopPath child chart.
          chartOptionsEnabled: false,
          dragHandleElement: null,
          hasUpdateDisplayOrDescription: false,
          isEmbedded: false,
        },
        chartData,
        settings,
        colorConfig,
        displayedMeasures,
        displayedSlices,
        __typename: 'TopPathsContext',
      } satisfies ITopPathsContext;
    } else {
      return {
        metadata: {
          ...metadata,
          display,
          description,
          mode,
          chartOptionsEnabled,
          dragHandleElement,
          hasUpdateDisplayOrDescription:
            display !== metadata.display ||
            description !== metadata.description,
          isEmbedded,
        },
        actions: {
          onDelete,
          updateDisplay,
          updateDescription,
          rebuildChart,
        },
        chartData,
        settings,
        colorConfig,
        displayedMeasures,
        displayedSlices,
        permissions: {
          entityPermissions,
          entityPermissionsHint,
        },
        __typename: 'BaseChartContext',
      } satisfies IBaseChartContext;
    }
  }, [
    display,
    description,
    chartOptionsEnabled,
    dragHandleElement,
    onDelete,
    updateDisplay,
    updateDescription,
    displayedMeasures,
    displayedSlices,
    colorConfig,
    settings,
    chartData,
    entityPermissions,
    entityPermissionsHint,
    mode,
    metadata,
    isEmbedded,
    rebuildChart,
  ]);

  return (
    <ChartContext.Provider value={contextValues}>
      {children}
    </ChartContext.Provider>
  );
};
